/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { Chip, Tooltip } from '@mui/material';
import { Schedule, CheckCircle, Cancel, Block } from '@mui/icons-material';

// Add local dependencies.
import {
  RequestStatusBadgeProps,
  JoinRequestStatus,
} from '../../../types/joinRequest';

/**
 * RequestStatusBadge component displays the status of a join request
 * with appropriate colors, icons, and tooltips.
 */
const RequestStatusBadge = ({
  status,
  submittedAt,
  reviewedAt,
  size = 'medium',
  showTooltip = true,
}: RequestStatusBadgeProps) => {
  const getStatusConfig = () => {
    switch (status) {
      case JoinRequestStatus.PENDING:
        return {
          label: 'Pending',
          color: 'warning' as const,
          icon: <Schedule fontSize="small" />,
          tooltip: submittedAt
            ? `Submitted on ${new Date(submittedAt).toLocaleDateString()}`
            : 'Awaiting review',
        };
      case JoinRequestStatus.APPROVED:
        return {
          label: 'Approved',
          color: 'success' as const,
          icon: <CheckCircle fontSize="small" />,
          tooltip: reviewedAt
            ? `Approved on ${new Date(reviewedAt).toLocaleDateString()}`
            : 'Request approved',
        };
      case JoinRequestStatus.REJECTED:
        return {
          label: 'Rejected',
          color: 'error' as const,
          icon: <Cancel fontSize="small" />,
          tooltip: reviewedAt
            ? `Rejected on ${new Date(reviewedAt).toLocaleDateString()}`
            : 'Request rejected',
        };
      case JoinRequestStatus.CANCELLED:
        return {
          label: 'Cancelled',
          color: 'default' as const,
          icon: <Block fontSize="small" />,
          tooltip: 'Request cancelled by user',
        };
      default:
        return {
          label: 'Unknown',
          color: 'default' as const,
          icon: null,
          tooltip: 'Unknown status',
        };
    }
  };

  const config = getStatusConfig();

  const chipSize = size === 'large' ? 'medium' : size;

  const badge = (
    <Chip
      label={config.label}
      color={config.color}
      size={chipSize}
      icon={config.icon || undefined}
      variant="filled"
      sx={{
        fontWeight: 'medium',
        '& .MuiChip-icon': {
          fontSize: size === 'small' ? '14px' : '16px',
        },
      }}
    />
  );

  if (showTooltip && config.tooltip) {
    return (
      <Tooltip title={config.tooltip} arrow>
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

export default RequestStatusBadge;
